import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Logo, WordMark } from "@/components/Logo";
import { LayoutDashboard, Settings, LogOut, ShieldCheck, Users, BarChart3, Plus, CreditCard } from "lucide-react";

export default function DashboardLayout({ children, role = "consultant" }) {
  const { user, consultant, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isActive = (p) => location.pathname === p || location.pathname.startsWith(p + "/");

  const nav = role === "superadmin" ? [
    { to: "/admin", label: "Panoramica", icon: BarChart3 },
    { to: "/admin/consultants", label: "Consulenti", icon: Users },
    { to: "/admin/subscriptions", label: "Abbonamenti", icon: CreditCard },
    { to: "/admin/settings", label: "Impostazioni", icon: Settings },
  ] : [
    { to: "/dashboard", label: "Pratiche", icon: LayoutDashboard },
    { to: "/dashboard/new", label: "Nuova pratica", icon: Plus },
    { to: "/dashboard/billing", label: "Abbonamento", icon: CreditCard },
    { to: "/dashboard/settings", label: "Impostazioni studio", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-[#0F172A] text-white border-r border-slate-800">
        <div className="px-6 py-5 border-b border-slate-800">
          <Logo className="h-12" variant="dark" />
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {nav.map((item) => (
            <Link key={item.to} to={item.to}
              data-testid={`sidebar-${item.to.replace(/\//g, "-")}-link`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold transition-colors ${
                isActive(item.to)
                  ? "bg-[#F77F2A] text-white"
                  : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`}>
              <item.icon className="w-4 h-4" />
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-slate-800">
          <div className="px-3 mb-3">
            <div className="text-xs text-slate-400 uppercase tracking-wider">Account</div>
            <div className="text-sm font-bold truncate">{user?.email}</div>
            {role === "consultant" && consultant && (
              <div className="text-xs text-slate-400 truncate">{consultant.studio_name}</div>
            )}
            {role === "superadmin" && (
              <div className="text-xs text-orange-400 flex items-center gap-1 mt-0.5"><ShieldCheck className="w-3 h-3" /> SuperAdmin</div>
            )}
          </div>
          <button onClick={handleLogout} data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-bold text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition">
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </aside>

      {/* Mobile top */}
      <div className="lg:hidden fixed top-0 inset-x-0 bg-[#0F172A] text-white z-30 border-b border-slate-800">
        <div className="px-4 h-16 flex items-center justify-between">
          <Logo className="h-12" variant="dark" />
          <button onClick={handleLogout} data-testid="logout-mobile-button" className="text-sm text-slate-300">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
        <nav className="px-2 pb-2 flex gap-1 overflow-x-auto">
          {nav.map((item) => (
            <Link key={item.to} to={item.to}
              className={`px-3 py-1.5 rounded-md text-xs font-bold whitespace-nowrap ${
                isActive(item.to) ? "bg-[#F77F2A] text-white" : "text-slate-300 bg-white/5"
              }`}>
              {item.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 lg:pt-0 pt-[100px]">
        {children}
      </main>
    </div>
  );
}
